import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog as MuiDialog,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
  type DialogProps,
  type DialogTitleProps as MuiDialogTitleProps,
} from '@mui/material';
import { unstable_useId as useId } from '@mui/material/utils';
import { createContext, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '~/components/IconButton';
import { LiveRegion } from '~/components/LiveRegion';

// The id the dialog's title text carries, which names the dialog (WCAG 4.1.2). MUI's own
// DialogContext does the same but is not part of its public API.
const TitleIdContext = createContext<string | undefined>(undefined);

export const Dialog = ({ 'aria-labelledby': ariaLabelledby, children, ...props }: DialogProps) => {
  const theme = useTheme();
  const fullscreen = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = useId(ariaLabelledby);
  return (
    <TitleIdContext.Provider value={titleId}>
      <MuiDialog aria-labelledby={titleId} fullScreen={fullscreen} {...props}>
        {children}
        {/* The page is aria-hidden behind an open dialog, so announcements made inside it
            need a region of their own; it hands back to the root as the dialog closes. */}
        <LiveRegion active={props.open} />
      </MuiDialog>
    </TitleIdContext.Provider>
  );
};

export interface DialogTitleProps extends Omit<MuiDialogTitleProps, 'component' | 'typography'> {
  onClose: (...args: never[]) => void;
}

/** The title names its dialog: its text, not the close button beside it, carries the title id. */
export const DialogTitle = ({ children, id, onClose, ...props }: DialogTitleProps) => {
  const { t } = useTranslation();
  const titleId = useContext(TitleIdContext);
  return (
    <Box
      sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      {...props}
    >
      <Typography component="h2" id={id ?? titleId} variant="h3">
        {children}
      </Typography>
      <IconButton label={t('common.actions.close')} onClick={onClose} size="large">
        <Close fontSize="small" />
      </IconButton>
    </Box>
  );
};

/** The dialog's lead text. Give it an `id` when it also names the dialog's one control. */
export const DialogContentTitle = ({ children, id }: { children: ReactNode; id?: string }) => {
  return (
    <Typography id={id} sx={{ mb: 2 }} variant="subtitle1">
      {children}
    </Typography>
  );
};

export const DialogForm = styled('form')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),

  // A role=group row (two inputs under one heading) lays out like a label row.
  '& label, & [role="group"]': {
    alignItems: 'baseline',
    display: 'flex',
    fontSize: 14,

    '.label': {
      flexBasis: '25%',
      flexShrink: 0,
    },

    '.required::after': {
      content: '" *"',
      color: theme.palette.error.dark,
    },

    '& img': {
      maxHeight: '8rem',
      maxWidth: '8rem',
      background: `#eee url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" fill-opacity=".15"><rect x="5" width="5" height="5" /><rect y="5" width="5" height="5" /></svg>');`,
      backgroundSize: '10px 10px',
      boxShadow: '0 0 0 1px rgba(63,63,68,0.15), 0 1px 3px 0 rgba(63,63,68,0.25)',
    },
  },
}));
